/**
 * WorkshopTicketBlock — the universal checkout entry point shared by every
 * workshop detail page (generic event-detail.tsx pages AND the bespoke
 * Claude for Design page).
 *
 * The on-page "Reserve your seat" panel was SUNSET on 2026-07-02. When the
 * workshop is bookable this component renders ONLY two portals:
 *   - the mobile sticky reserve bar (sm:hidden)
 *   - the MobileCheckoutSheet — full-screen sheet below `sm`, right-anchored
 *     drawer from `sm` up — opened via lib/open-checkout window events
 *
 * When NOTHING is bookable (every upcoming date sold out, or a single-date
 * workshop that sold out), it renders a compact full-bleed sold-out band
 * instead. That band carries id={sectionId} so legacy #tickets / #reserve
 * anchor fallbacks still land somewhere meaningful.
 *
 * All pricing / early-bird / series-slug resolution lives in
 * bookableSessions below; the sheet never re-derives money.
 */
import { useState, useEffect, useMemo } from "react";
import { VIP_SESSION } from "@shared/vip-session";
import { createPortal } from "react-dom";
import { Link } from "wouter";
import { WavyMark } from "@/components/ui/wavy-mark";
import { MobileCheckoutSheet, KidsCrayonTitle, KIDS_CRAYON_COLORS_ON_DARK, type BookableSession, type TicketOption } from "@/components/checkout/MobileCheckoutSheet";
import { CHECKOUT_OPEN_EVENT } from "@/lib/open-checkout";
import type { ClockResult } from "@/components/scarcity/useSeatClock";
import type { EventWithSeries } from "@shared/schema";
import { KIDS_SERIES_SLUG, KIDS_COHORT_SCHEDULE } from "@shared/kids-camp";
import { effectiveScrollY } from "@/lib/body-scroll-lock";

/** Crayon strip for the kids reserve bar's top edge — the six kids-scope
 *  hues as hard-stop segments (the bar portals into document.body, outside
 *  .kids-scope, so the page's --kids-* vars don't reach it). */
const KIDS_BAR_STRIPE =
  "linear-gradient(90deg, hsl(224,68%,46%) 0 16.66%, #D94000 16.66% 33.33%, hsl(41,96%,48%) 33.33% 50%, hsl(145,52%,36%) 50% 66.66%, hsl(330,66%,48%) 66.66% 83.33%, hsl(262,52%,50%) 83.33% 100%)";


export function formatDate(dateStr: string, tz: string) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: tz,
  });
}

export function formatTime(startStr: string, endStr: string, tz: string) {
  const s = new Date(startStr);
  const e = new Date(endStr);
  return `${s.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz })} – ${e.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz, timeZoneName: "short" })}`;
}

export function formatPrice(cents: number) {
  // Whole dollars stay clean ("$999"); fractional-cent amounts (a 15% coupon
  // off $999 → $849.15) render BOTH decimal places — the default dropped the
  // trailing zero and printed "$1,400.8".
  const whole = cents % 100 === 0;
  return `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * 2026-09-09 — this was a scroll reveal, and it is now a plain wrapper.
 *
 * It shipped every section at `opacity-0 translate-y-5` and faded it in on an
 * IntersectionObserver with `rootMargin: "-60px"`. Anything the reader skipped
 * past — an anchor/jump-nav link, the End key, a scrollbar drag — landed on a
 * blank section that only appeared after scrolling back through it, and with
 * Lenis's scroll tail the fade fired while the page was still decelerating, so
 * the content slid while the page slid. The name and props stay so every
 * caller compiles unchanged.
 */
export function RevealSection({
  children,
  id,
  className = "",
  style,
}: {
  children: React.ReactNode;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <section id={id} style={style} className={className}>
      {children}
    </section>
  );
}

export function SiteEyebrow({ label, variant = "terra", align = "left" }: { label: string; variant?: "terra" | "cream"; align?: "left" | "center" }) {
  // Label + hairline + wavy mark all take the variant color.
  const labelColor = variant === "cream" ? "var(--cream)" : "var(--terra)";
  return (
    <div
      className={`flex items-center gap-3 mb-10 ${align === "center" ? "justify-center" : ""}`}
      style={{ color: labelColor }}
    >
      <span className="type-eyebrow" style={{ marginBottom: 0 }}>{label}</span>
      <div className="h-px flex-1 max-w-[120px]" style={{ backgroundColor: labelColor, opacity: variant === "cream" ? 0.4 : 0.5 }} />
      <WavyMark size={14} color={labelColor} />
    </div>
  );
}

// Per-series override for the "Go VIP" addon (label, description, price,
// and whether it's offered alongside Team-mode purchases). Defaults to the
// cowork values. The server-side equivalent is in server/routes.ts under
// the same key — both sides MUST stay in sync on priceCents or buyers will
// see one number on the page and a different one in Stripe checkout.
type PremiumAddonConfig = {
  label: string;
  description: string;
  priceCents: number;
  /** When false, the addon is hidden in Team mode. Defaults to true. */
  allowOnTeam?: boolean;
};
const DEFAULT_PREMIUM_ADDON: PremiumAddonConfig = {
  label: "Go VIP",
  description: "90-minute private 1-on-1 training session with a senior AI engineer.",
  // The VIP 1:1 product (shared/vip-session.ts), also sold alone at /vip-session.
  priceCents: VIP_SESSION.priceCents,
  allowOnTeam: true,
};
const PREMIUM_ADDON_CONFIG_BY_SERIES: Record<string, PremiumAddonConfig> = {
  "the-ai-first-leader-workshop": {
    label: "Go VIP+",
    description: "45-minute private strategy call with Patrick after the workshop.",
    priceCents: 30000,
    allowOnTeam: false,
  },
  "advanced-claude-code-workshop": {
    label: "Go VIP",
    description: "45-minute live audit of your shipped Claude app, run by Nechama after the workshop.",
    priceCents: 30000,
    allowOnTeam: false,
  },
  "claude-for-design-workshop": {
    label: "Go VIP",
    description: "90-minute private 1-on-1 training session with a senior AI engineer.",
    priceCents: VIP_SESSION.priceCents,
    allowOnTeam: true,
  },
  /* $999, the same as Claude Design, which is what Esther asked for on
     2026-09-01 ("whatever we've been running for claude design") and confirmed
     again on 2026-09-09. It was written as $300 in between. The entry stays
     explicit rather than falling through to the $999 default because the
     DESCRIPTION is series-specific and rides on the Stripe line item.
     MUST stay in sync with server/routes.ts on priceCents. */
  "claude-for-sales-teams-workshop": {
    label: "Go VIP",
    description: "Private 45-minute deal-coaching session with Brigid on one of your real deals, after the workshop.",
    priceCents: VIP_SESSION.priceCents,
    allowOnTeam: true,
  },
};

export type CouponStatus = {
  valid: boolean;
  percentOff?: number;
  fixedPriceCents?: number | null;
  label?: string;
  message?: string;
};

export interface WorkshopTicketBlockProps {
  event: EventWithSeries;
  /** Anchor id — only rendered on the sold-out band (the bookable state is
   *  portals-only, no section). The generic pages use the default "tickets";
   *  the design page passes "reserve" to keep its #reserve anchor links
   *  landing somewhere when everything is sold out. */
  sectionId?: string;
  selectedSessionId: number | null;
  onSessionSelect: (id: number) => void;
  /** Email captured upstream (e.g. reminder popup ?email= param) — pre-fills
   *  the Stripe checkout email field. */
  prefillEmail?: string;
  /** UTM params captured from the landing URL, forwarded to checkout. */
  utmParams?: Record<string, string | undefined>;
  /** Seat-clock state per upcoming session (scarcity-enabled series only).
   *  The parent owns this because the hero shares the same clocks. */
  sessionClocks?: Record<number, ClockResult>;
  /** Coupon code captured upstream (e.g. ?coupon= URL param) — pre-fills the
   *  checkout sheet's coupon field. */
  initialCouponCode?: string;
  /** Code page price test, arm A ("no price at the top"): the mobile sticky
   *  bar leaves out its price so the arm doesn't see one after a short
   *  scroll. The checkout sheet still shows prices once opened. */
  hideBarPrice?: boolean;
}

export function WorkshopTicketBlock({
  event,
  sectionId = "tickets",
  selectedSessionId,
  prefillEmail,
  utmParams = {},
  sessionClocks = {},
  initialCouponCode,
  hideBarPrice = false,
}: WorkshopTicketBlockProps) {
  const seatsRemaining = event.maxCapacity ? event.maxCapacity - (event.seatsSold || 0) : null;
  // Claude Code for Kids: family mode. NO VIP addon exists for this series
  // (without this, the $999 DEFAULT_PREMIUM_ADDON fallback would appear),
  // and the sheet swaps its seat stepper for per-camper rows + consents.
  const isKidsCamp = event.seriesSlug === KIDS_SERIES_SLUG;
  const premiumAddonCfg = isKidsCamp
    ? null
    : (event.seriesSlug && PREMIUM_ADDON_CONFIG_BY_SERIES[event.seriesSlug]) || DEFAULT_PREMIUM_ADDON;

  // ── Mobile checkout takeover ──
  // A taller mobile-only sticky bar ("Reserve spot" + the next dates) opens a
  // full-screen MobileCheckoutSheet instead of scrolling back up to this form.
  // Desktop is unaffected (everything here is gated sm:hidden / open state).
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  // `?vip=1` deep link (recap emails): open the sheet and go straight to the
  // VIP-standalone checkout. No effect without the param or without a VIP addon.
  const [autoVipOnly, setAutoVipOnly] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !premiumAddonCfg) return;
    if (new URLSearchParams(window.location.search).get("vip") === "1") {
      setAutoVipOnly(true);
      setMobileSheetOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // The sticky mobile reserve bar is the ONLY mobile ticket affordance (the old
  // top "Get Tickets" bar was removed). It must be persistently available once
  // the buyer scrolls past the hero, not gated on the ticket section being in
  // view — so we reveal it after the first viewport-height of scroll and keep it
  // pinned for the rest of the page. Desktop never renders it (sm:hidden).
  const [showReserveBar, setShowReserveBar] = useState(false);
  // Portal target: only render the fixed bar on the client (avoids SSR/hydration
  // mismatch and lets it escape this section's transformed ancestor).
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onScroll = () => setShowReserveBar(effectiveScrollY() > window.innerHeight * 0.6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // URL-captured coupon (?coupon=META25 from paid ads): validate once at page
  // level so the reserve bar can show the discounted price with a strike
  // BEFORE the sheet ever opens. Display-only — the sheet re-validates on
  // open and the server recomputes authoritatively at checkout. Scope rules
  // (kids-only vs global) ride on seriesSlug, same as the sheet's validator.
  const [urlCouponStatus, setUrlCouponStatus] = useState<CouponStatus | null>(null);
  useEffect(() => {
    const code = initialCouponCode?.trim();
    if (!code) {
      setUrlCouponStatus(null);
      return;
    }
    let cancelled = false;
    fetch("/api/validate-coupon", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code, seriesSlug: event.seriesSlug ?? event.slug }),
    })
      .then((r) => r.json())
      .then((status: CouponStatus) => {
        if (!cancelled) setUrlCouponStatus(status);
      })
      .catch(() => {
        /* display-only — the sheet's own validation still runs on open */
      });
    return () => {
      cancelled = true;
    };
  }, [initialCouponCode, event.seriesSlug, event.slug]);
  const upcomingSessions = useMemo(() => {
    const nowMs = Date.now();
    return (event.seriesSessions ?? [])
      .filter((s) => new Date(s.startDatetime).getTime() >= nowMs)
      .sort((a, b) => new Date(a.startDatetime).getTime() - new Date(b.startDatetime).getTime());
  }, [event.seriesSessions]);

  // Pre-resolve every bookable option for the mobile checkout sheet — one per
  // upcoming session (or a single synthesized option for a one-date workshop),
  // each carrying its Individual + Team tiers. All pricing / early-bird /
  // series-slug logic lives here so the sheet never re-derives money.
  const bookableSessions: BookableSession[] = useMemo(() => {
    // Team-tier detection MUST mirror the server's isTeamTicket() in
    // server/routes.ts (buildCheckoutSessionParams) — a tier the client sells
    // as "individual" that the server treats as team (or vice versa) would
    // strand buyers at a 400 or leak the team rate at quantity 1.
    const isTeamLabel = (label: string) => {
      const l = label.toLowerCase();
      return l.includes("team") || l.includes("group");
    };
    const isIndividualLabel = (label: string) =>
      ["standard", "individual", "general admission"].includes(label.trim().toLowerCase());
    const resolveTickets = (tts: typeof event.ticketTypes): TicketOption[] => {
      const std = tts.find((t) => isIndividualLabel(t.label)) || tts[0];
      // Case-insensitive team/group match first; fall back to the second tier
      // positionally (the old on-page panel's behavior) ONLY when it's cheaper
      // than std — guards against a pricier VIP-style second tier being
      // misread as team pricing.
      const team =
        tts.find((t) => isTeamLabel(t.label)) ??
        (std && tts[1] && tts[1] !== std && tts[1].priceCents < std.priceCents ? tts[1] : undefined);
      const toOption = (t: typeof std, kind: "individual" | "team"): TicketOption | null => {
        if (!t) return null;
        const eb = !!t.earlyBirdPriceCents && !!t.earlyBirdDeadline && new Date() < new Date(t.earlyBirdDeadline);
        return {
          id: t.id,
          kind,
          priceCents: eb ? t.earlyBirdPriceCents! : t.priceCents,
          fullPriceCents: t.priceCents,
          earlyBird: eb,
          minQty: kind === "team" ? 3 : 1,
          maxQty: t.maxQty || (kind === "team" ? 20 : 10),
        };
      };
      return [toOption(std, "individual"), team && team !== std ? toOption(team, "team") : null].filter(
        Boolean,
      ) as TicketOption[];
    };
    if (upcomingSessions.length > 0) {
      return upcomingSessions
        // Sold-out dates are not bookable — they must never appear in the
        // sheet's date dropdown or the reserve bar's counts.
        .filter((s) => s.status !== "sold_out")
        .map((s) => {
          const tts = s.ticketTypes && s.ticketTypes.length > 0 ? s.ticketTypes : event.ticketTypes;
          // Prefer the scarcity clock's seat count (what the hero + ticket
          // tiles display) so the sheet never contradicts the page.
          const clockSeats = sessionClocks[s.id]?.seatsLeft;
          return {
            id: s.id,
            session: s,
            effectiveSlug: s.slug ?? event.slug,
            seatsRemaining:
              typeof clockSeats === "number"
                ? clockSeats
                : s.maxCapacity != null
                  ? s.maxCapacity - (s.seatsSold ?? 0)
                  : null,
            tickets: resolveTickets(tts),
          };
        })
        .filter((b) => b.tickets.length > 0);
    }
    // Single-date workshop — synthesize one option from the primary event,
    // unless that date itself is sold out (nothing bookable).
    if (event.status === "sold_out") return [];
    const tickets = resolveTickets(event.ticketTypes);
    if (tickets.length === 0) return [];
    return [
      {
        id: event.id,
        session: {
          id: event.id,
          slug: event.slug,
          status: event.status,
          startDatetime: event.startDatetime,
          endDatetime: event.endDatetime,
          timezone: event.timezone,
          zoomJoinUrl: event.zoomJoinUrl,
          seatsSold: event.seatsSold,
          maxCapacity: event.maxCapacity,
          ticketTypes: event.ticketTypes,
        },
        effectiveSlug: event.slug,
        seatsRemaining,
        tickets,
      },
    ];
  }, [upcomingSessions, event, seatsRemaining, sessionClocks]);

  // Page-wide CTAs (hero, jump-nav, persona cards, final band) open the
  // checkout via requestCheckout() → window event at EVERY breakpoint —
  // full-screen sheet below `sm`, right-anchored drawer from `sm` up.
  // preventDefault marks the event handled so callers skip their legacy
  // anchor-scroll fallback; when nothing is bookable we don't listen and
  // callers fall through to scrolling to the (sold-out) ticket section.
  useEffect(() => {
    if (bookableSessions.length === 0) return;
    const onOpenRequest = (e: Event) => {
      e.preventDefault();
      setMobileSheetOpen(true);
    };
    window.addEventListener(CHECKOUT_OPEN_EVENT, onOpenRequest);
    return () => window.removeEventListener(CHECKOUT_OPEN_EVENT, onOpenRequest);
  }, [bookableSessions.length]);

  // ── Sold-out / nothing-bookable state ──
  // Every upcoming date is sold out (or the single date is). The reserve bar
  // and checkout sheet stay unrendered, and no CHECKOUT_OPEN_EVENT listener
  // is attached — page CTAs fall through to their legacy anchor-scroll
  // fallback, which needs a real target. This full-bleed band carries
  // id={sectionId} so those #tickets / #reserve scrolls land on actual
  // sold-out messaging instead of a silent no-op.
  if (bookableSessions.length === 0) {
    return (
      <RevealSection
        id={sectionId}
        style={{ marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }}
      >
        <div
          className="px-6 md:px-12 lg:px-20 py-16 md:py-20"
          style={{ backgroundColor: "var(--charcoal)" }}
        >
          <div className="max-w-[1280px] mx-auto">
            <SiteEyebrow label="Live, hands-on training" variant="terra" />
            <h2 className="type-h2 mb-4" style={{ color: "var(--cream)" }}>
              Sold out
            </h2>
            <p className="type-body text-cream/75 max-w-[52ch] mb-8">
              Every upcoming date for this workshop is sold out. New dates are
              announced by email first.
            </p>
            <Link href="/workshops" className="btn btn-cta" data-testid="sold-out-all-workshops">
              See all workshops
            </Link>
          </div>
        </div>
      </RevealSection>
    );
  }

  return (
    // The on-page "Reserve your seat" section was SUNSET on 2026-07-02 —
    // the universal checkout (full-screen sheet on mobile, right drawer on
    // desktop) is the ONLY checkout flow. This component now renders just
    // the two portals: the mobile sticky reserve bar and the checkout
    // sheet/drawer, both opened via lib/open-checkout.
    <>

      {/* ── Mobile-only: sticky reserve bar that opens the takeover ──
          Maven-style: price (+ strikethrough list price) with a seats-left
          urgency chip, next date + time underneath, solid terra CTA on the
          right. Tapping opens the full-screen MobileCheckoutSheet (no
          scroll-back-up). Desktop never renders this (sm:hidden). */}
      {mounted && createPortal(
        (() => {
          // bookableSessions is non-empty past the sold-out return above, and
          // every entry has at least one ticket (empty ones are filtered out).
          const barSession =
            bookableSessions.find((b) => b.id === selectedSessionId) ?? bookableSessions[0];
          const barTicket = barSession.tickets.find((t) => t.kind === "individual") ?? barSession.tickets[0];
          // Percent coupon from the URL (?coupon=META25): the bar leads with
          // the discounted price and strikes the price the buyer would pay
          // WITHOUT the code (the early-bird effective price, not list — the
          // strike demonstrates exactly what the code saves). Fixed-price
          // codes skip this: their math is per-order, not per-ticket.
          const barPct =
            urlCouponStatus?.valid && !urlCouponStatus.fixedPriceCents && urlCouponStatus.percentOff
              ? urlCouponStatus.percentOff
              : 0;
          const barPriceCents =
            barPct > 0 ? Math.round(barTicket.priceCents * (1 - barPct / 100)) : barTicket.priceCents;
          const barStrikeCents = barPct > 0 ? barTicket.priceCents : barTicket.fullPriceCents;
          const barStrike =
            (barPct > 0 || barTicket.earlyBird) && barStrikeCents > barPriceCents;
          const barSeats = barSession.seatsRemaining;
          const barStart = new Date(barSession.session.startDatetime as unknown as string);
          const barTz = barSession.session.timezone;
          return (
            <div
              className={`fixed bottom-0 left-0 right-0 z-[110] motion-reduce:transition-none ${isKidsCamp ? "" : "border-t border-cream/15 sm:hidden"}`}
              style={{
                backgroundColor: "var(--charcoal)",
                paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
                opacity: showReserveBar ? 1 : 0,
                transform: showReserveBar ? "translateY(0)" : "translateY(100%)",
                pointerEvents: showReserveBar ? "auto" : "none",
                // visibility:hidden (after the fade) removes the hidden bar
                // from BOTH the a11y tree and the tab order — aria-hidden
                // alone leaves the CTA keyboard-focusable.
                visibility: showReserveBar ? "visible" : "hidden",
                transition:
                  "opacity 300ms cubic-bezier(0.25,0,0.25,1), transform 300ms cubic-bezier(0.25,0,0.25,1), visibility 300ms",
              }}
              data-testid="mobile-reserve-bar"
              aria-hidden={!showReserveBar}
              /* Portaled to <body>, so it needs its own landmark — without
                 one, its content sits outside every region (axe "region"). */
              role="region"
              aria-label="Reserve your spot"
            >
              {/* Kids: the top edge is a six-hue crayon strip instead of the
                  hairline — the bar reads as part of the page's crayon world
                  without lightening the charcoal (contrast sells the CTA). */}
              {isKidsCamp && (
                <div className="h-[3px] w-full" style={{ background: KIDS_BAR_STRIPE }} aria-hidden="true" />
              )}
              <div className={`flex items-center gap-3 px-4 pt-3 ${isKidsCamp ? "md:max-w-[1280px] md:mx-auto md:px-8 md:pt-4 md:gap-6" : ""}`}>
                <div className="min-w-0 flex-1">
                  {/* Kids ≥md: lead with the program's NAME (multicolor
                      "Kids", same treatment as the checkout sheet) and tell
                      the savings story in words — the bare $999 said nothing
                      about why (Isaac 2026-07-22). Mobile keeps the compact
                      price rows below. */}
                  {isKidsCamp && (
                    /* One row (Isaac 2026-08-06): title, then the price/
                       savings facts on the same baseline — the bar is wide
                       enough that stacking just wasted height. */
                    <div className="hidden md:flex items-baseline gap-5 min-w-0" data-testid="kids-bar-desktop">
                      <p className="m-0 text-[19px] font-bold text-cream leading-none whitespace-nowrap" style={{ letterSpacing: "-0.01em" }}>
                        <KidsCrayonTitle title={event.title} colors={KIDS_CRAYON_COLORS_ON_DARK} />
                      </p>
                      <p className="m-0 text-[13px] text-cream/70 leading-tight whitespace-nowrap min-w-0">
                        {barPct > 0 ? (
                          /* URL coupon (paid-ads landing): the strike does the
                             talking — price without the code, price with it,
                             and the discount named in gold. */
                          <>
                            <s className="text-cream/45 tabular-nums">{formatPrice(barStrikeCents)}</s>{" "}
                            <span className="font-semibold text-cream tabular-nums text-[15px]">
                              {formatPrice(barPriceCents)}
                            </span>{" "}
                            <span className="font-semibold" style={{ color: "hsl(41, 96%, 55%)" }}>
                              {barPct}% off applied
                            </span>
                          </>
                        ) : barStrike ? (
                          <>
                            <span className="font-semibold" style={{ color: "hsl(41, 96%, 55%)" }}>
                              Save {formatPrice(barTicket.fullPriceCents - barTicket.priceCents)}
                            </span>{" "}
                            with early bird pricing until August 10
                          </>
                        ) : (
                          KIDS_COHORT_SCHEDULE.barLine
                        )}
                        {barSeats != null && barSeats > 0 && barSeats < 20 && (
                          <span
                            className="font-mono text-[11px] uppercase tracking-wide whitespace-nowrap"
                            style={{ color: "var(--terra)" }}
                          >
                            {" "}· {barSeats} spots left
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                  <div className={isKidsCamp ? "md:hidden" : ""}>
                  <p className="flex flex-wrap items-baseline gap-2 leading-none">
                    {!hideBarPrice && (
                    <span className="text-[20px] font-semibold text-cream tabular-nums" data-testid="bar-price">
                      {formatPrice(barPriceCents)}
                    </span>
                    )}
                    {!hideBarPrice && barStrike && (
                      /* Hidden below 380px — on the narrowest phones the
                         strikethrough pushed the seats-left chip under the CTA. */
                      <span className="hidden min-[380px]:inline text-[13px] text-cream/40 line-through tabular-nums">
                        {formatPrice(barStrikeCents)}
                      </span>
                    )}
                    {/* Kids: the discount gets its NAME, hand-lettered — the
                        bare strike never explains why the price dropped. A
                        URL coupon outranks the early-bird label. */}
                    {isKidsCamp && barStrike && (
                      <span
                        className="hidden min-[420px]:inline whitespace-nowrap"
                        style={{ fontFamily: "var(--font-hand)", fontWeight: 700, fontSize: "14px", letterSpacing: "0.4px", color: "hsl(41, 96%, 55%)" }}
                        data-testid="kids-bar-earlybird"
                      >
                        {barPct > 0 ? `${barPct}% OFF` : "EARLY BIRD"}
                      </span>
                    )}
                    {barSeats != null && barSeats > 0 && barSeats < 20 && (
                      <span
                        className="text-[11px] font-mono uppercase tracking-wide whitespace-nowrap"
                        style={{ color: "var(--terra)" }}
                      >
                        {barSeats} {isKidsCamp ? "spots" : "seats"} left
                      </span>
                    )}
                  </p>
                  {/* Kids: no date/session line (Isaac 2026-08-06) — the
                      price row carries the bar; schedule detail lives in the
                      checkout sheet it opens. */}
                  {!isKidsCamp && (
                  <p className="text-[12px] text-cream/60 mt-1.5 leading-tight line-clamp-1">
                    {barStart && barTz
                      ? `${barStart.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: barTz })} · ${barStart.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: barTz, timeZoneName: "short" })}`
                      : new Date(event.startDatetime).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: event.timezone })}
                    {bookableSessions.length > 1 && (
                      /* The hook, not a style: on a 390 or 360 phone this line
                         has no room for the extra clause and `line-clamp-1`
                         cuts it mid-word, so the row ends on a dangling "· +1…".
                         The Sales scope hides it under 400px (see
                         `.ds-sales-page [data-bar-more]` in cowork-ds.css); the
                         attribute is inert everywhere else, so Cowork, Code and
                         Design render exactly as they did. */
                      <span data-bar-more className="text-cream/40"> · +{bookableSessions.length - 1} more</span>
                    )}
                  </p>
                  )}
                  </div>
                </div>
                <button
                  type="button"
                  /* The bar is aria-hidden until it slides in, so its CTA leaves
                     the tab order with it — otherwise a keyboard lands on a button
                     that is not on screen yet. */
                  tabIndex={showReserveBar ? undefined : -1}
                  onClick={() => setMobileSheetOpen(true)}
                  className={`shrink-0 h-12 px-6 font-mono text-[14px] tracking-[0.8px] uppercase focus-visible:ring-2 focus-visible:ring-cream ${isKidsCamp ? "md:h-14 md:px-9" : "rounded-[4px] transition-[filter] hover:brightness-90"}`}
                  style={
                    isKidsCamp
                      ? { backgroundColor: "#7150c0", color: "var(--cream)", touchAction: "manipulation", borderRadius: "999px", boxShadow: "4px 4px 0 #43297d", transition: "transform 90ms ease, box-shadow 90ms ease" }
                      : { backgroundColor: "var(--terra)", color: "var(--cream)", touchAction: "manipulation" }
                  }
                  onMouseDown={isKidsCamp ? (e) => { e.currentTarget.style.transform = "translate(4px,4px)"; e.currentTarget.style.boxShadow = "0 0 0 #43297d"; } : undefined}
                  onMouseUp={isKidsCamp ? (e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "4px 4px 0 #43297d"; } : undefined}
                  onMouseLeave={isKidsCamp ? (e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "4px 4px 0 #43297d"; } : undefined}
                  data-testid="mobile-reserve-bar-cta"
                >
                  {isKidsCamp ? "Register now" : event.seriesSlug === "claude-for-beginners-workshop" ? "Reserve your seat" : "Reserve seat"}
                </button>
              </div>
            </div>
          );
        })(),
        document.body,
      )}

      <MobileCheckoutSheet
        open={mobileSheetOpen}
        onClose={() => setMobileSheetOpen(false)}
        event={event}
        sessions={bookableSessions}
        initialSessionId={selectedSessionId ?? bookableSessions[0].id}
        premiumAddon={
          premiumAddonCfg
            ? {
                label: premiumAddonCfg.label,
                description: premiumAddonCfg.description,
                priceCents: premiumAddonCfg.priceCents,
                allowOnTeam: premiumAddonCfg.allowOnTeam !== false,
              }
            : null
        }
        prefillEmail={prefillEmail}
        utmParams={utmParams}
        initialCouponCode={initialCouponCode}
        kids={isKidsCamp ? { termsHref: "/claude-code-for-kids/program-terms" } : null}
        autoVipOnly={autoVipOnly}
      />
    </>
  );
}
